import {
  FaroBulkActionsButtonsProps,
  FaroTableBulkActionsButtons,
} from "@components/common/faro-table/faro-table-bulk-actions-buttons";
import { FaroTextButton } from "@components/common/faro-text-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { IconButton, neutral, Text, CloseIcon } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Divider, Stack } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { sidebarSelector } from "@store/ui/ui-selector";
import { withEllipsis } from "@styles/common-styles";
import {
  SIDEBAR_WIDTH_EXPANDED,
  SIDEBAR_WIDTH_MINIFIED,
} from "@utils/ui-utils";

/** Returns the current sidebar pixel width based on screen size and sidebar state */
function getSidebarWidth(
  isScreenSmOrLarger: boolean,
  sidebar: { isVisible: boolean; isFullSize: boolean }
): number {
  if (!isScreenSmOrLarger || !sidebar.isVisible) {
    return 0;
  }
  return sidebar.isFullSize ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_MINIFIED;
}

interface Props extends FaroBulkActionsButtonsProps {
  /** Number of rows that is selected in the table */
  numberOfSelectedRows: number;

  /** All available row IDs in the table */
  allRowIds: string[];

  /** Whether the select all action is available */
  isSelectAllAvailable?: boolean;
}

/**
 * Renders a floating action bar at the bottom of the table when rows are selected.
 *
 * Displays the number of selected rows, action buttons for bulk operations,
 * and a close button to clear the selection. The bar is absolutely positioned
 * within the table container and appears with a dark background.
 */
export function FaroTableBulkActions({
  bulkActionsButtons,
  numberOfSelectedRows,
  isLoading,
  allRowIds,
  isSelectAllAvailable = false,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const mode = useBulkActionsMode();
  const { isScreenSmOrLarger } = useMediaQueryList();
  const sidebar = useAppSelector(sidebarSelector);

  // On ≥600px screens the permanent sidebar shifts content but position:fixed ignores it.
  // Offset the bar so it centres within the content area, not the full viewport.
  const sidebarWidth = getSidebarWidth(isScreenSmOrLarger, sidebar);

  function handleSelectAll(): void {
    if (allRowIds && allRowIds.length > 0) {
      dispatch(setInitialFetchingItems(allRowIds));
    }
  }

  // Always render bulk action buttons (they contain dialogs that may need to show success messages)
  // But only show the visual bulk actions bar when rows are selected
  // to avoid this we need to create a refactor that separates the message dialogs from the actionButtons
  if (!numberOfSelectedRows) {
    return (
      <Stack position="absolute">
        <FaroTableBulkActionsButtons
          bulkActionsButtons={bulkActionsButtons}
          isLoading={isLoading}
        />
      </Stack>
    );
  }
  const isCollapsedMode = mode === "collapsed";

  return (
    <Stack
      sx={{
        flexDirection: "row",
        position: "fixed",
        bottom: 20,
        left: `calc(50% + ${sidebarWidth / 2}px)`,
        transform: "translateX(-50%)",
        borderRadius: "4px",
        background: neutral[950],
        height: 48,
        alignItems: "center",
        px: 1.5,
        zIndex: 99,
        gap: 1.5,
        whiteSpace: "nowrap",
      }}
    >
      <SphereTooltip
        title={`${numberOfSelectedRows} selected`}
        shouldShowTooltip
      >
        <Stack
          sx={{
            maxWidth: "5rem",
          }}
        >
          <Text
            component="var"
            variant="bodyM"
            color={neutral[0]}
            sx={{
              ...withEllipsis,
            }}
          >
            {numberOfSelectedRows} selected
          </Text>
        </Stack>
      </SphereTooltip>

      {(isCollapsedMode || isSelectAllAvailable) && allRowIds.length > 1 && (
        <FaroTextButton
          onClick={handleSelectAll}
          tooltipText={"Select all"}
          rippleColor={neutral[500]}
          sx={{
            color: neutral[0],
            fontSize: "0.875rem",
          }}
        >
          Select all
        </FaroTextButton>
      )}

      <Divider
        orientation="vertical"
        sx={{ height: 36, borderColor: neutral[800] }}
      />

      <FaroTableBulkActionsButtons
        bulkActionsButtons={bulkActionsButtons}
        isLoading={isLoading}
      />

      <Divider
        orientation="vertical"
        sx={{ height: 36, borderColor: neutral[800] }}
      />

      <IconButton
        size="m"
        margin={0.5}
        color={neutral[0]}
        hoverColor={neutral[0]}
        onClick={() => dispatch(setInitialFetchingItems([]))}
      >
        <CloseIcon />
      </IconButton>
    </Stack>
  );
}
